import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Result, Spin, Typography, Descriptions, Tag } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import axios from 'axios'
import { API_URL } from '@/config/api'

const { Title, Text } = Typography

const STATO_LABEL: Record<string, string> = {
  ATTESA: 'In attesa di conferma',
  CONFERMATA: 'Confermata',
  IN_LAVORAZIONE: 'In lavorazione',
  COMPLETATA: 'Completata',
  ANNULLATA: 'Annullata',
}

const STATO_COLOR: Record<string, string> = {
  ATTESA: 'orange',
  CONFERMATA: 'blue',
  IN_LAVORAZIONE: 'processing',
  COMPLETATA: 'success',
  ANNULLATA: 'error',
}

interface PrenotazionePubblica {
  codice: string
  stato: string
  data_ora: string
  tipo_servizio: string
  note?: string
  cliente?: string
  veicolo?: string
  qr_usato: boolean
}

export default function TireCheckinPublicPage() {
  const { codice } = useParams<{ codice: string }>()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<PrenotazionePubblica | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!codice) return
    axios
      .get(`${API_URL}/tire-service/public/prenotazione/${codice}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Prenotazione non trovata'))
      .finally(() => setLoading(false))
  }, [codice])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: 24 }}>
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title="Prenotazione non trovata"
          subTitle={error || 'Il codice QR non è valido o la prenotazione è stata annullata.'}
        />
      </div>
    )
  }

  const dataOra = new Date(data.data_ora)
  const dataStr = dataOra.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  const oraStr = dataOra.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

  return (
    <div style={{ maxWidth: 520, margin: '40px auto', padding: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
        <Title level={3} style={{ marginBottom: 4 }}>Prenotazione Cambio Gomme</Title>
        <Text type="secondary">Codice: {data.codice}</Text>
      </div>

      <Descriptions bordered column={1} size="middle">
        <Descriptions.Item label="Stato">
          <Tag color={STATO_COLOR[data.stato]}>{STATO_LABEL[data.stato] ?? data.stato}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Data">
          {dataStr}
        </Descriptions.Item>
        <Descriptions.Item label="Orario">
          {oraStr}
        </Descriptions.Item>
        <Descriptions.Item label="Servizio">
          {data.tipo_servizio}
        </Descriptions.Item>
        {data.cliente && (
          <Descriptions.Item label="Intestatario">{data.cliente}</Descriptions.Item>
        )}
        {data.veicolo && (
          <Descriptions.Item label="Veicolo">{data.veicolo}</Descriptions.Item>
        )}
        {data.note && (
          <Descriptions.Item label="Note">{data.note}</Descriptions.Item>
        )}
      </Descriptions>

      {data.stato === 'ANNULLATA' && (
        <Result
          status="warning"
          title="Prenotazione annullata"
          subTitle="Contatta l'officina per maggiori informazioni."
          style={{ marginTop: 24, padding: 0 }}
        />
      )}
    </div>
  )
}
